import { DOSSIER_STATUT_LABELS, type DossierDto } from '../../lib/api-types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

/**
 * Read-only guidance card: « Complétude / prochaines étapes ». It summarises
 * what a non-expert user might do next, derived ONLY from data already present
 * on the dossier (scenario, objet, available transitions) plus the biens count
 * fetched by the detail page. It invents NO server rules and is explicitly
 * labelled as guidance — the authoritative workflow stays server-driven.
 */
export function DossierCompletenessPanel({
  dossier,
  biensCount,
}: {
  dossier: DossierDto;
  /** Number of biens on the dossier; `undefined` while still loading/unknown. */
  biensCount?: number;
}): JSX.Element {
  const hasObjet = Boolean(dossier.objet && dossier.objet.trim().length > 0);
  const hasBiens = typeof biensCount === 'number' ? biensCount > 0 : undefined;
  const transitions = dossier.availableTransitions;

  const checklist: { label: string; done: boolean | undefined; hint?: string }[] = [
    {
      label: 'Objet du dossier renseigné',
      done: hasObjet,
      hint: 'Ajoutez un objet via « Modifier » pour décrire la mission.',
    },
    {
      label: 'Au moins un bien enregistré',
      done: hasBiens,
      hint: 'Ajoutez un bien dans l’onglet « Structure immobilière ».',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complétude / prochaines étapes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">
          Guide indicatif pour vous aider à avancer. Les règles officielles de validation restent
          appliquées par le serveur.
        </p>

        <ul className="space-y-2">
          {checklist.map((item) => (
            <li key={item.label} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className={
                  item.done === true
                    ? 'mt-0.5 text-accent-600'
                    : item.done === false
                      ? 'mt-0.5 text-slate-300'
                      : 'mt-0.5 text-slate-300'
                }
              >
                {item.done === true ? '✓' : '○'}
              </span>
              <div>
                <p className="text-sm text-slate-800">
                  {item.label}
                  {item.done === undefined ? ' (vérification en cours…)' : ''}
                </p>
                {item.done === false && item.hint ? (
                  <p className="text-xs text-slate-500">{item.hint}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Transitions possibles
          </p>
          {transitions.length > 0 ? (
            <p className="mt-1 text-sm text-slate-700">
              Vous pouvez faire passer le dossier vers :{' '}
              <span className="font-medium">
                {transitions.map((t) => DOSSIER_STATUT_LABELS[t]).join(', ')}
              </span>{' '}
              (via « Changer le statut »).
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-700">
              Ce dossier est à un statut terminal ou bloqué : aucune transition n’est disponible
              pour le moment.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
